/**
 * Lernzeit (F-22 Stufe 7): Stoppuhr pro Fach, Nachtragen, Wochendiagramm
 * (gestapelt nach Fachfarbe) und Lernzeit je Fach - alles direkt sichtbar.
 */
import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Play, Plus, Square } from 'lucide-react';

import { Input } from '@/ui/primitives/Input';
import { Button } from '@/ui/primitives/Button';
import { AreaDot } from '@/modules/areas/AreaDot';
import { db } from '@/db/schema';
import { lernzeitNachtragen, starteLernzeit, stoppeLernzeit } from '@/db/repositories/studyTime';
import {
  dauerText, lernzeitJeWoche, lernzeitProFach, minutenVon, uhrText, wochenLernzeit,
} from '@/domain/school/studyTime';
import { addDays, formatDe, startOfWeek, today } from '@/lib/datetime';
import { Saeulen } from '@/ui/composites/Saeulen';
import type { StudySession, Subject } from '@/domain/types';

/** Laufende Uhr: einmal pro Sekunde neu zeichnen, nur solange sie laeuft. */
function useJetzt(laeuft: boolean): Date {
  const [jetzt, setJetzt] = useState(() => new Date());
  useEffect(() => {
    if (!laeuft) return;
    const t = window.setInterval(() => setJetzt(new Date()), 1000);
    return () => window.clearInterval(t);
  }, [laeuft]);
  return laeuft ? jetzt : new Date();
}

export function Lernzeit({ faecher }: { faecher: Subject[] }) {
  const sitzungen = useLiveQuery(() => db.studySessions.toArray(), [], [] as StudySession[]);
  const laufend = sitzungen.find((s) => !s.end);
  const jetzt = useJetzt(!!laufend);
  const heute = today();
  const [fach, setFach] = useState('');
  const fachId = fach || laufend?.subjectId || faecher[0]?.id || '';
  const [nachDatum, setNachDatum] = useState(heute);
  const [nachMin, setNachMin] = useState('');

  const farbe = new Map(faecher.map((f) => [f.id, f.color]));
  const name = new Map(faecher.map((f) => [f.id, f.name]));
  // Auf der Fachseite nur dieses Fach (V-16) - die Stoppuhr oben kennt trotzdem alle.
  const ids = new Set(faecher.map((f) => f.id));
  const eigene = sitzungen.filter((s) => ids.has(s.subjectId));
  const woche = wochenLernzeit(eigene, heute, jetzt);
  const maxTag = Math.max(60, ...woche.map((d) => d.summe));
  const dieseWoche = lernzeitProFach(eigene, jetzt, startOfWeek(heute));
  const gesamt = lernzeitProFach(eigene, jetzt);
  const wochenSumme = woche.reduce((a, d) => a + d.summe, 0);
  // Verlauf ueber 8 Wochen (F-02) - in Stunden, eine Nachkommastelle.
  const verlauf = lernzeitJeWoche(eigene, heute, 8, jetzt);

  if (faecher.length === 0) return null;

  return (
    <section aria-label="Lernzeit" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <header className="flex items-baseline gap-2">
        <h2 className="flex-1 text-sm font-semibold">Lernzeit</h2>
        <span className="text-[12px] text-ink-muted">Diese Woche {dauerText(wochenSumme)}</span>
      </header>

      {/* Stoppuhr */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={fachId} onChange={(e) => setFach(e.target.value)} disabled={!!laufend} aria-label="Fach für die Stoppuhr"
          className="h-9 rounded-md border border-line bg-surface px-2 text-sm disabled:opacity-60">
          {faecher.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        {laufend ? (
          <>
            <span role="timer" aria-label="Laufende Lernzeit" className="text-xl font-semibold tabular-nums text-accent-text">
              {uhrText(minutenVon(laufend, jetzt))}
            </span>
            <Button size="sm" variant="primary" onClick={() => void stoppeLernzeit()}>
              <Square className="size-3.5" /> Stopp
            </Button>
          </>
        ) : (
          <Button size="sm" variant="primary" onClick={() => void starteLernzeit(fachId)}>
            <Play className="size-3.5" /> Start
          </Button>
        )}
      </div>

      {/* Wochendiagramm: Mo-So, gestapelt nach Fach */}
      <div>
        <ol aria-label="Lernzeit je Tag dieser Woche" className="flex h-24 items-end gap-1.5">
          {woche.map((d) => {
            const text = `${formatDe(d.date, 'EEEEEE dd.MM.')}: ${dauerText(d.summe)}`;
            return (
              <li key={d.date} title={text} aria-label={text} className="flex h-full min-w-0 flex-1 flex-col justify-end">
                <div className="flex flex-col-reverse overflow-hidden rounded-sm" style={{ height: `${(d.summe / maxTag) * 100}%` }}>
                  {[...d.proFach].map(([id, min]) => (
                    <span key={id} className="block w-full" style={{ height: `${(min / Math.max(d.summe, 1)) * 100}%`, backgroundColor: farbe.get(id) ?? 'var(--color-line-strong)' }} />
                  ))}
                </div>
                {d.summe === 0 && <span className="block h-0.5 w-full rounded-sm bg-surface-raised" />}
              </li>
            );
          })}
        </ol>
        <div className="mt-1 flex gap-1.5 text-center text-[10px] text-ink-faint">
          {woche.map((d) => (
            <span key={d.date} className={d.date === heute ? 'flex-1 font-semibold text-ink' : 'flex-1'}>{formatDe(d.date, 'EEEEEE')}</span>
          ))}
        </div>
      </div>

      <Saeulen
        label="Lernzeit je Woche (8 Wochen)"
        einheit="h"
        punkte={verlauf.map((w) => ({
          key: w.start,
          titel: `KW ${w.kw} (${formatDe(w.start, 'dd.MM.')}–${formatDe(addDays(w.start, 6), 'dd.MM.')})`,
          wert: Math.round(w.minuten / 6) / 10,
        }))}
        format={(h) => h.toLocaleString('de-DE')}
        links={`KW ${verlauf[0]!.kw}`}
        rechts="diese Woche"
      />

      {/* Je Fach */}
      <ul className="flex flex-col gap-1 text-sm">
        {faecher.filter((f) => (gesamt.get(f.id) ?? 0) > 0).map((f) => (
          <li key={f.id} className="flex items-center gap-2">
            <AreaDot color={f.color} size="sm" />
            <span className="min-w-0 flex-1 truncate">{f.name}</span>
            <span className="shrink-0 tabular-nums text-ink-muted">{dauerText(dieseWoche.get(f.id) ?? 0)}</span>
            <span className="w-24 shrink-0 text-right text-[12px] tabular-nums text-ink-faint">gesamt {dauerText(gesamt.get(f.id) ?? 0)}</span>
          </li>
        ))}
        {gesamt.size === 0 && <li className="text-ink-faint">Noch keine Lernzeit erfasst.</li>}
      </ul>

      {/* Nachtragen */}
      <div className="flex flex-wrap items-end gap-2 border-t border-line pt-2.5">
        <span className="text-[12px] text-ink-faint">Nachtragen für {name.get(fachId)}:</span>
        <div className="w-40"><Input type="date" value={nachDatum} max={heute} onChange={(e) => setNachDatum(e.target.value)} aria-label="Tag der Lernzeit" className="h-9 text-sm" /></div>
        <div className="w-24"><Input value={nachMin} inputMode="numeric" onChange={(e) => setNachMin(e.target.value)} placeholder="Minuten" aria-label="Minuten" className="h-9 text-sm" /></div>
        <Button size="sm" variant="ghost" disabled={!(Number(nachMin) > 0)}
          onClick={async () => { if (await lernzeitNachtragen(fachId, nachDatum, Number(nachMin))) setNachMin(''); }}>
          <Plus className="size-3.5" /> Eintragen
        </Button>
      </div>
    </section>
  );
}
